import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import ActionMenu, { ActionButton } from '../../action-buttons/ActionButtons';
import Loader from '../../loader/Loader';
import PagedTable from '../../sorted-table/PagedTable';
import { buildODataParams } from '../../../pages/market-place/MarketPlace';
import { FilterSelection } from '../../../pages/market-place/MarketPlaceFilters';
import { MarketPlaceSpecimenResponse } from '../../../types/interfaces/marketPlace.interfaces';
import { PagedResponse, StandardParams } from '../../../types/interfaces/apiParams.interfaces';
import { getMarketplaceSpecimensOData } from '../../../api/marketPlaceSpecimenApi';
import { BUTTON_CONSTANTS, MARKETPLACE_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { showToast } from '../../../services/toast.service';
import './specimenTableItem.scss';

type Props = {
  search: string;
  sort?: string;
  selection?: FilterSelection;
  isAddToCartDisabled: (specimen: MarketPlaceSpecimenResponse) => boolean;
  onPreview: (specimen: MarketPlaceSpecimenResponse) => void;
  onAddToCart: (specimen: MarketPlaceSpecimenResponse) => void;
};

const PAGE_SIZE = 20;

const SpecimenTableItem: FC<Props> = ({ search, sort, selection, isAddToCartDisabled, onPreview, onAddToCart }): JSX.Element => {
  const [specimens, setSpecimens] = useState<MarketPlaceSpecimenResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const callNumber: MutableRefObject<number> = useRef(0);
  const hasAllResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef(false);

  const filterKey = [search, sort, JSON.stringify(selection ?? {})].join('|');

  const getSpecimens = (params: StandardParams = {}) =>
    getMarketplaceSpecimensOData(buildODataParams(search, sort, selection, params.offset ?? 0, params.limit ?? PAGE_SIZE), true);

  const getRows = (data: MarketPlaceSpecimenResponse[] | PagedResponse<MarketPlaceSpecimenResponse>) =>
    Array.isArray(data) ? data : data.result ?? [];

  const loadMobileData = async (currentOffset: number) => {
    if (hasAllResults.current) return;

    callNumber.current++;
    const callNum = callNumber.current;

    try {
      setLoading(true);
      const response = await getSpecimens({ offset: currentOffset, limit: PAGE_SIZE });

      if (callNum !== callNumber.current) return;

      const result = getRows(response.data);
      setSpecimens(prev => (currentOffset === 0 ? result : [...prev, ...result]));
      hasAllResults.current = result.length === 0;
    } catch {
      showToast.error(toastMessages.FAILED_TO_FETCH_MARKETPLACE_SPECIMENS);
    } finally {
      if (callNum === callNumber.current) {
        setLoading(false);
        isLoadingAdditionalPage.current = false;
      }
    }
  };

  useEffect(() => {
    if (window.innerWidth > 768) {
      setLoading(false);
      return;
    }

    hasAllResults.current = false;
    setLoading(true);
    setSpecimens([]);
    setOffset(0);
    void loadMobileData(0);
  }, [filterKey]);

  useEffect(() => {
    if (window.innerWidth > 768 || offset === 0) return;
    void loadMobileData(offset);
  }, [offset]);

  useEffect(() => {
    isLoadingAdditionalPage.current = false;
  }, [specimens]);

  useEffect(() => {
    if (window.innerWidth > 768) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const position = scrollHeight - scrollTop;
      const atBottom = position - clientHeight <= clientHeight;

      if (atBottom && !isLoadingAdditionalPage.current) {
        isLoadingAdditionalPage.current = true;
        setOffset(prev => prev + PAGE_SIZE);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const renderActionMenu = (specimen: MarketPlaceSpecimenResponse) => {
    const actionButtons: ActionButton[] = [{ name: BUTTON_CONSTANTS.VIEW_PROFILE, action: () => onPreview(specimen) }];
    if (!isAddToCartDisabled(specimen)) {
      actionButtons.push({ name: BUTTON_CONSTANTS.ADD_TO_CART, action: () => onAddToCart(specimen) });
    }
    return <ActionMenu actionButtons={actionButtons} />;
  };

  return (
    <div className="marketplace-specimen-table">
      <div className="desk-marketplace-specimen-table">
        <PagedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.NAME },
            { displayName: TABLE_HEADER_CONSTANTS.BREED },
            { displayName: MARKETPLACE_CONSTANTS.SPECIES },
            { displayName: TABLE_HEADER_CONSTANTS.GENDER },
            { displayName: MARKETPLACE_CONSTANTS.STOCK },
            { displayName: TABLE_HEADER_CONSTANTS.ACTION },
          ]}
          filter={filterKey}
          searchString={search || undefined}
          getData={getSpecimens}
          height={700}
          pageSize={PAGE_SIZE}
          scrollBufferThickness={100}
          noResultsMessage={MARKETPLACE_CONSTANTS.NO_MARKETPLACE_SPECIMEN}
          buildRow={(specimen: MarketPlaceSpecimenResponse) => [
            specimen.animal?.name ?? '--',
            specimen.animal?.breed?.name ?? '--',
            specimen.animal?.breed?.species?.name ?? '--',
            specimen.animal?.gender ?? '--',
            specimen.quantity ?? '--',
            renderActionMenu(specimen),
          ]}
        />
      </div>

      {/* Mobile View */}
      <div className="xs-marketplace-specimen-table">
        {!loading && specimens.length === 0 ? (
          <div className="result-item">{MARKETPLACE_CONSTANTS.NO_MARKETPLACE_SPECIMEN}</div>
        ) : (
          specimens.map(specimen => (
            <div className="card max-width" key={specimen.marketPlaceSpecimenId}>
              <div className="specimen-card-row">
                <div className="specimen-content">
                  <div className="specimen-row">
                    <div className="specimen-cell">
                      <label>{TABLE_HEADER_CONSTANTS.NAME}: </label>
                      <label>{specimen.animal?.name ?? '--'}</label>
                    </div>
                    <div className="specimen-cell">
                      <label>{TABLE_HEADER_CONSTANTS.BREED}: </label>
                      <label>{specimen.animal?.breed?.name ?? '--'}</label>
                    </div>
                    <div className="specimen-cell">
                      <label>{MARKETPLACE_CONSTANTS.SPECIES}: </label>
                      <label>{specimen.animal?.breed?.species?.name ?? '--'}</label>
                    </div>
                  </div>

                  <div className="specimen-row">
                    <div className="specimen-cell">
                      <label>{TABLE_HEADER_CONSTANTS.GENDER}: </label>
                      <label>{specimen.animal?.gender ?? '--'}</label>
                    </div>
                    <div className="specimen-cell">
                      <label>{MARKETPLACE_CONSTANTS.STOCK}: </label>
                      <label>{specimen.quantity ?? '--'}</label>
                    </div>
                  </div>
                </div>

                <div className="action-button">{renderActionMenu(specimen)}</div>
              </div>
            </div>
          ))
        )}
        {loading && <Loader addedSpace loaderSize="small" />}
      </div>
    </div>
  );
};

export default SpecimenTableItem;
